import React, { useContext, useEffect, useMemo, useState } from "react";
import "./section-navigation.component.scoped.scss";
import { Box, Typography } from "@material-ui/core";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import { useTranslation } from "react-i18next";
import { services } from "../../../../../services";
import { LessonDetails, LessonStatus } from "../../../types/lesson.types";
import LessonCardComponent from "../../../shared/lesson-card/lesson-card.component";
import CheckpointCardComponent from "../../../shared/checkpoint-card/checkpoint-card.component";
import { history } from "react-router-guard";
import { educationRoutes } from "../../../types/education.routes";
import { UserRoles } from "../../../../../types/userRoles";
import { GlobalContext } from "../../../../../store";

const SectionNavigationComponent = ({
  lessonDetails,
  lessonId,
  loading,
}: {
  lessonDetails: LessonDetails;
  lessonId: string;
  loading: boolean;
}) => {
  const { t } = useTranslation();
  const [section, setSection] = useState<any>({});
  const { user } = useContext(GlobalContext);

  const hasFullAccess = UserRoles.FULL_ACCESS_ROLES.includes(user.role);

  const isAccessible = (item: any) => {
    if (hasFullAccess) {
      return true;
    }

    return item.status !== LessonStatus.INACTIVE;
  };

  useEffect(() => {
    const sectionId = lessonDetails?.sectionId;
    if (!sectionId) return;

    let cancelled = false;

    const loadSection = async () => {
      try {
        const data = await services.courses.getLearnerSection(
          String(sectionId),
        );
        if (!cancelled) setSection(data);
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching section:", error);
          setSection(null);
        }
      }
    };

    void loadSection();

    return () => {
      cancelled = true;
    };
  }, [
    lessonDetails?.sectionId,
    lessonDetails?.id,
    lessonDetails?.userStatus?.status,
  ]);

  const navigateToItem = (item: any) => {
    if (item.type === "lesson") {
      history.push(educationRoutes.lesson(item.id));
    }

    if (item.type === "checkpoint") {
      history.push(
        hasFullAccess
          ? educationRoutes.checkpoint(item.id)
          : educationRoutes.exam(item.id),
      );
    }
  };

  const navigationItems = useMemo(() => {
    return [
      ...(section?.lessons ?? []).map((lesson: any) => ({
        ...lesson,
        type: "lesson" as const,
      })),
      // ...(section?.checkpoints ?? []).map((checkpoint: any) => ({
      //   ...checkpoint,
      //   type: "checkpoint" as const,
      // })),
    ];
  }, [section]);

  const currentIndex = useMemo(
    () =>
      navigationItems.findIndex(
        (item) => item.type === "lesson" && String(item.id) === lessonId,
      ),
    [navigationItems, lessonId],
  );

  const previousItem = useMemo(() => {
    for (let index = currentIndex - 1; index >= 0; index--) {
      const item = navigationItems[index];

      if (isAccessible(item)) {
        return item;
      }
    }

    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationItems, currentIndex]);

  const nextItem = useMemo(() => {
    for (
      let index = currentIndex + 1;
      index < navigationItems.length;
      index++
    ) {
      const item = navigationItems[index];

      if (isAccessible(item)) {
        return item;
      }
    }

    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationItems, currentIndex]);

  return (
    <Box className="section__navigation__content">
      <Box
        className="grid-2 justify-content-between mb-4"
        style={{ gap: "1.9rem" }}
      >
        {previousItem && (
          <Box
            onClick={() => navigateToItem(previousItem)}
            className="global__box__content d-flex align-items-center"
            style={{
              padding: "15px 2rem",
              gap: "1rem",
              cursor: "pointer",
              gridColumn: "1",
            }}
          >
            <ArrowBackIosIcon style={{ fontSize: "24px" }} />
            <Box className="d-flex flex-column">
              <Typography
                variant="h4"
                color="textPrimary"
                className="font-weight-bold"
              >
                {t("BACK")}
              </Typography>
              <Typography variant="body1" color="textSecondary">
                {previousItem.title}
              </Typography>
            </Box>
          </Box>
        )}
        {nextItem && (
          <Box
            onClick={() => {
              if (nextItem.locked) return;
              navigateToItem(nextItem);
            }}
            className="global__box__content d-flex align-items-center"
            style={{
              padding: "15px 2rem",
              gap: "1rem",
              justifyContent: "flex-end",
              cursor: nextItem.locked ? "default" : "pointer",
              gridColumn: "2",
            }}
          >
            <Box className="d-flex flex-column text-right">
              <Typography
                variant="h4"
                color="textPrimary"
                className="font-weight-bold"
              >
                {t("FORWARD")}
              </Typography>
              <Typography variant="body1" color="textSecondary">
                {nextItem.title}
              </Typography>
            </Box>
            <ArrowForwardIosIcon style={{ fontSize: "24px" }} />
          </Box>
        )}
      </Box>

      {!hasFullAccess && (
        <Box className="section__navigation__content__lists mb-4">
          {section.lessons && section.lessons.length ? (
            <>
              {(section?.lessons ?? []).map((lesson: any) => (
                <Box key={lesson.id} className="lesson__card">
                  <LessonCardComponent
                    lesson={lesson}
                    loading={loading}
                    sectionLocked={section.locked}
                  />
                </Box>
              ))}
            </>
          ) : null}
          {section.checkpoints && section.checkpoints.length ? (
            <>
              {(section?.checkpoints ?? []).map((checkpoint: any) => (
                <Box key={checkpoint.id} className="checkpoint__card">
                  <CheckpointCardComponent
                    checkpoint={checkpoint}
                    loading={loading}
                    sectionLocked={section.locked}
                  />
                </Box>
              ))}
            </>
          ) : null}
        </Box>
      )}
    </Box>
  );
};

export default SectionNavigationComponent;
